const CACHE_NAME = 'us-i18n-packs'

type Pack = Record<string, unknown>

/** External packs are opt-in; built-in locales never require a discovery request. */
export function externalTranslationIndex(explicit?: string) {
  const configured =
    explicit ??
    document.querySelector<HTMLMetaElement>('meta[name="us-i18n-index"]')
      ?.content
  if (!configured) return null
  try {
    const url = new URL(configured, window.location.href)
    return url.origin === window.location.origin &&
      /^https?:$/.test(url.protocol)
      ? url
      : null
  } catch {
    return null
  }
}

async function readJson(url: string) {
  // Offline is a normal state: consult the installed public pack, not the network.
  const cache =
    typeof caches === 'undefined'
      ? undefined
      : await caches.open(CACHE_NAME).catch(() => undefined)
  const response =
    navigator.onLine === false
      ? await cache?.match(url)
      : await fetch(url, { credentials: 'omit' })
  if (
    !response?.ok ||
    !response.headers.get('content-type')?.includes('application/json')
  )
    return null
  const value: unknown = await response.clone().json()
  if (cache && navigator.onLine !== false)
    await cache.put(url, response).catch(() => {})
  return value
}

export async function loadExternalTranslations(index: URL) {
  const result: Record<string, Pack> = {}
  const manifest = await readJson(index.href)
  if (
    !manifest ||
    typeof manifest !== 'object' ||
    !('langs' in manifest) ||
    !Array.isArray(manifest.langs)
  )
    return result
  for (const raw of manifest.langs) {
    if (
      typeof raw !== 'string' ||
      !/^[a-z]{2,3}(?:-[a-z0-9]{2,8})*$/i.test(raw)
    )
      continue
    const locale = raw.toLowerCase()
    try {
      const pack = await readJson(new URL(locale + '.json', index).href)
      if (pack && typeof pack === 'object' && !Array.isArray(pack))
        result[locale] = pack as Pack
    } catch {
      // A failed optional pack must not replace the available built-in dictionary.
    }
  }
  return result
}
